import React, { useState, useEffect } from 'react';
import { DropDownComponent, InputComponent, FileUploadComponent } from '../custcomponents';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { AxiosAdminInstance } from '../utils';

interface AwardJury {
    id: string;
    name: string;
    linkedin: string;
    imageurl: string;
}

export const ManageAwardJury = () => {
    const { eventcode } = useParams<{ eventcode: string }>();
    const [juryMembers, setJuryMembers] = useState<AwardJury[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingJury, setEditingJury] = useState<AwardJury | null>(null);
    const [newJury, setNewJury] = useState({
        name: '',
        linkedin: '',
        imageFile: null as File | null,
        imageurl: '',
        contentType: ''
    });

    useEffect(() => {
        fetchJuryMembers();
    }, []);

    useEffect(() => {
        if (editingJury) {
            setNewJury({
                name: editingJury.name,
                linkedin: editingJury.linkedin,
                imageFile: null,
                imageurl: editingJury.imageurl,
                contentType: ''
            });
        } else {
            setNewJury({
                name: '',
                linkedin: '',
                imageFile: null,
                imageurl: '',
                contentType: ''
            });
        }
    }, [editingJury]);

    const fetchJuryMembers = async () => {
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/manageAwardJury', {
                action: 'getJuryMembers',
                data: { eventcode }
            });
            if (response.data.success) {
                const juryObj = response.data.juryMembers || {};
                const juryArray = Object.keys(juryObj).map(id => ({
                    id,
                    ...juryObj[id]
                }));
                setJuryMembers(juryArray);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to fetch jury members');
        }
        setLoading(false);
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const base64 = reader.result as string;
                // Remove the data:image/jpeg;base64, prefix
                const base64Data = base64.split(',')[1];
                resolve(base64Data);
            };
            reader.onerror = error => reject(error);
        });
    };

    const handleAddJury = async () => {
        if (!newJury.name.trim()) {
            toast.error('Jury member name is required');
            return;
        }
        setLoading(true);
        try {
            let data = {
                eventcode,
                name: newJury.name,
                linkedin: newJury.linkedin,
                imageurl: newJury.imageurl
            };
            if (editingJury) {
                data = { ...data, juryId: editingJury.id } as any;
            }
            if (newJury.imageFile) {
                const imageBase64 = await fileToBase64(newJury.imageFile);
                data = { ...data, imageBase64, imageFileName: newJury.imageFile.name, contentType: newJury.contentType } as any;
            }
            const response = await AxiosAdminInstance.post('/api/admin/manageAwardJury', {
                action: editingJury ? 'editJuryMember' : 'addJuryMember',
                data
            });
            if (response.data.success) {
                toast.success(editingJury ? 'Jury member updated successfully' : 'Jury member added successfully');
                setNewJury({
                    name: '',
                    linkedin: '',
                    imageFile: null,
                    imageurl: '',
                    contentType: ''
                });
                setEditingJury(null);
                fetchJuryMembers();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(editingJury ? 'Failed to update jury member' : 'Failed to add jury member');
        }
        setLoading(false);
    };

    const handleCancel = () => {
        setNewJury({
            name: '',
            linkedin: '',
            imageFile: null,
            imageurl: '',
            contentType: ''
        });
        setEditingJury(null);
    };

    const handleDeleteJury = async (juryId: string) => {
        if (!window.confirm('Are you sure you want to delete this jury member?')) return;
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/manageAwardJury', {
                action: 'deleteJuryMember',
                data: { eventcode, juryId }
            });
            if (response.data.success) {
                toast.success('Jury member deleted successfully');
                fetchJuryMembers();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to delete jury member');
        }
        setLoading(false);
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<boolean> => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return false;
            }
            // Validate file size (150KB max)
            if (file.size > 150 * 1024) {
                toast.error('File size must be less than 150KB');
                return false;
            }
            setNewJury(prev => ({ ...prev, imageFile: file, imageurl: '', contentType: file.type }));
            return true;
        }
        return false;
    };

    const juryColumns = [
        { name: 'Actions', cell: (row: AwardJury) => (
            <div>
                <button className="btn btn-sm btn-warning me-1" onClick={() => setEditingJury(row)} title="Edit">
                    <i className="fa fa-edit"></i>
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDeleteJury(row.id)} title="Delete">
                    <i className="fa fa-trash"></i>
                </button>
            </div>
        ) },
        { name: 'Name', selector: (row: AwardJury) => row.name, sortable: true },
        { name: 'LinkedIn', selector: (row: AwardJury) => row.linkedin, sortable: true },
        { name: 'Image', cell: (row: AwardJury) => (
            row.imageurl ? <img src={row.imageurl} alt={row.name} style={{ width: '50px', height: '50px', objectFit: 'cover' }} /> : 'No Image'
        ) }
    ];

    return (
        <>
            <section className="register-sec sec-padding-admin" data-scroll-index="8">
                <div className="container">
                    <div className="sec-title">
                        <span className="icon-inner wow flipInY" data-wow-duration="300ms">
                            <span className="fa-stack">
                                <i className="fa rhex fa-stack-2x"></i>
                                <i className="fa fa-users fa-stack-1x"></i>
                            </span>
                        </span>
                        <h3 className="wow fadeInRight" data-wow-duration="500ms">Manage Award Jury</h3>
                    </div>
                    <div className="register-sec-details">
                        <form onSubmit={(e) => { e.preventDefault(); handleAddJury(); }}>
                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">{editingJury ? 'Edit Jury Member' : 'Add New Jury Member'}</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="name"
                                            custplaceholder="Jury Member Name"
                                            custid="juryName"
                                            onExternalValue={(val: string) => setNewJury(prev => ({ ...prev, name: val }))}
                                            onExternalBind={newJury.name}
                                        />
                                    </div>
                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="linkedin"
                                            custplaceholder="LinkedIn Profile URL (optional)"
                                            custid="juryLinkedIn"
                                            onExternalValue={(val: string) => setNewJury(prev => ({ ...prev, linkedin: val }))}
                                            onExternalBind={newJury.linkedin}
                                        />
                                    </div>
                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                        <FileUploadComponent
                                            custname="image"
                                            custplaceholder="Upload Photo"
                                            custid="juryImage"
                                            onChange={handleFileChange}
                                        />
                                        {newJury.imageurl && (
                                            <div className="mt-2">
                                                <img src={newJury.imageurl} alt="Current" style={{ width: '50px', height: '50px', objectFit: 'cover' }} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        <button className="btn btn-primary" type="submit" disabled={loading}>
                                            {editingJury ? 'Update Jury Member' : 'Add Jury Member'} {loading && <i className="fa fa-circle-o-notch fa-spin"></i>}
                                        </button>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        {editingJury && (
                                            <button className="btn btn-secondary ms-2" type="button" onClick={handleCancel}>
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </fieldset>

                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">Award Jury Members</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <DataTable
                                            columns={juryColumns}
                                            data={juryMembers}
                                            customStyles={{
                                                cells: {
                                                    style: {
                                                        wordWrap: 'break-word',
                                                        whiteSpace: 'normal',
                                                        padding: '8px'
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};